import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { showSuccess, showError, confirmAction } from '@/lib/swal';
import { Venue } from '@/types';
import { VenueForm } from './VenueForm';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Users,
  ParkingCircle,
  Accessibility,
} from 'lucide-react';
import { formatDate } from '@/lib/utils';

interface VenuesTabProps {
  unitId: string;
}

export function VenuesTab({ unitId }: VenuesTabProps) {
  const { currentUser } = useAuth();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVenues();
  }, [unitId]);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      const venuesQuery = query(
        collection(db, 'units', unitId, 'venues'),
        where('deleted', '==', false)
      );
      const venuesSnapshot = await getDocs(venuesQuery);
      const venuesData = venuesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Venue[];

      // Sort by name
      venuesData.sort((a, b) => a.name.localeCompare(b.name));
      setVenues(venuesData);
    } catch (error) {
      console.error('Error fetching venues:', error);
      showError('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVenue = async (venueData: Partial<Venue>) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      // Remove undefined values - Firestore doesn't accept them
      const cleanedData = Object.fromEntries(
        Object.entries(venueData).filter(([_, value]) => value !== undefined)
      );

      const newVenue = {
        ...cleanedData,
        unitId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: currentUser.uid,
        deleted: false,
      };

      await addDoc(collection(db, 'units', unitId, 'venues'), newVenue);
      showSuccess('Venue created successfully!');
      setShowForm(false);
      await fetchVenues();
    } catch (error) {
      console.error('Error creating venue:', error);
      showError('Failed to create venue');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateVenue = async (venueData: Partial<Venue>) => {
    if (!editingVenue) return;

    setSaving(true);
    try {
      // Remove undefined values - Firestore doesn't accept them
      const cleanedData = Object.fromEntries(
        Object.entries(venueData).filter(([_, value]) => value !== undefined)
      );

      const updatedVenue = {
        ...cleanedData,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(doc(db, 'units', unitId, 'venues', editingVenue.id), updatedVenue);
      showSuccess('Venue updated successfully!');
      setEditingVenue(null);
      await fetchVenues();
    } catch (error) {
      console.error('Error updating venue:', error);
      showError('Failed to update venue');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVenue = async (venue: Venue) => {
    const result = await confirmAction({
      title: 'Delete Venue?',
      text: `Are you sure you want to delete "${venue.name}"? This action can be undone by contacting support.`,
      confirmButtonText: 'Delete',
      icon: 'warning',
    });

    if (!result.isConfirmed) return;

    try {
      // Soft delete
      await updateDoc(doc(db, 'units', unitId, 'venues', venue.id), {
        deleted: true,
        deletedAt: Timestamp.now(),
        deletedBy: currentUser?.uid,
      });

      showSuccess('Venue deleted successfully');
      await fetchVenues();
    } catch (error) {
      console.error('Error deleting venue:', error);
      showError('Failed to delete venue');
    }
  };

  const handleEdit = (venue: Venue) => {
    setEditingVenue(venue);
    setShowForm(false);
  };

  const handleCancelEdit = () => {
    setEditingVenue(null);
    setShowForm(false);
  };

  if (showForm || editingVenue) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {editingVenue ? 'Edit Venue' : 'Create New Venue'}
          </CardTitle>
          <CardDescription>
            {editingVenue
              ? 'Update the details of this venue'
              : 'Add a new venue to your unit'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <VenueForm
            venue={editingVenue}
            onSave={editingVenue ? handleUpdateVenue : handleCreateVenue}
            onCancel={handleCancelEdit}
            saving={saving}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Venues</h2>
          <p className="text-muted-foreground">
            Manage your unit's venues and locations
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Venue
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Loading venues...
          </CardContent>
        </Card>
      ) : venues.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Venues Yet</h3>
            <p className="text-muted-foreground mb-4">
              Add your first venue to start managing your unit locations.
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Venue
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {venues.map((venue) => (
            <Card key={venue.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      {venue.name}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={venue.status === 'active' ? 'default' : 'secondary'}>
                        {venue.status}
                      </Badge>
                      {venue.hasParking && (
                        <Badge variant="outline" className="gap-1">
                          <ParkingCircle className="h-3 w-3" />
                          Parking
                        </Badge>
                      )}
                      {venue.hasAccessibility && (
                        <Badge variant="outline" className="gap-1">
                          <Accessibility className="h-3 w-3" />
                          Accessible
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(venue)}
                      title="Edit venue"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteVenue(venue)}
                      className="text-destructive hover:text-destructive"
                      title="Delete venue"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">{venue.address}</span>
                </div>

                {venue.telephone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={`tel:${venue.telephone}`}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {venue.telephone}
                    </a>
                  </div>
                )}

                {venue.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={venue.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Visit Website
                    </a>
                  </div>
                )}

                {venue.capacity && (
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      Capacity: {venue.capacity}
                    </span>
                  </div>
                )}

                {(venue.email_address_office ||
                  venue.email_address_dining ||
                  venue.email_address_accounts) && (
                  <div className="border-t pt-3 mt-3 space-y-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      Contact Emails
                    </div>
                    {venue.email_address_office && (
                      <div className="pl-5 text-xs">
                        <span className="font-medium">Office:</span>{' '}
                        <a
                          href={`mailto:${venue.email_address_office}`}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          {venue.email_address_office}
                        </a>
                      </div>
                    )}
                    {venue.email_address_dining && (
                      <div className="pl-5 text-xs">
                        <span className="font-medium">Dining:</span>{' '}
                        <a
                          href={`mailto:${venue.email_address_dining}`}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          {venue.email_address_dining}
                        </a>
                      </div>
                    )}
                    {venue.email_address_accounts && (
                      <div className="pl-5 text-xs">
                        <span className="font-medium">Accounts:</span>{' '}
                        <a
                          href={`mailto:${venue.email_address_accounts}`}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          {venue.email_address_accounts}
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {venue.notes && (
                  <div className="border-t pt-3 mt-3">
                    <div className="text-xs font-semibold text-muted-foreground mb-1">
                      Notes:
                    </div>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                      {venue.notes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

